/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PickerProvider = require('./PictProvider-InputType-Picker.js');

const libTestResultOptions = require('./_TestResultOptions.js');

/** Length of the longest shared trailing run of two strings. */
const commonSuffixLength = (pLeft, pRight) =>
{
	let tmpCount = 0;
	while (tmpCount < pLeft.length && tmpCount < pRight.length &&
		pLeft[pLeft.length - 1 - tmpCount] === pRight[pRight.length - 1 - tmpCount])
	{
		tmpCount++;
	}
	return tmpCount;
};

/**
 * The result control is deliberately partial-width: a full-bleed control reads as its own region, which
 * is the confusion this input exists to remove. `min-width: 0` keeps the picker from forcing the flex
 * row wider than its column.
 *
 * The remarks field is a SEPARATE descriptor rendering its own column below this one, so by default the
 * two stack with ~90px between the picker and the editor. The `test-acceptance-*` rules close that gap
 * and lift the editor's View/Edit switch into the result row, right-aligned: the editor is positioned,
 * and its switch is taken out of flow with `bottom: 100%` so it lands in the space just above — the
 * result row, once the padding between the two columns is removed.
 *
 * The empty-state hint replaces pict-section-form's hardcoded "(empty)" (RichText `_mountView`, no config
 * hook). It is CSS rather than a DOM swap because PSF re-renders the view on every View/Edit switch, which
 * would overwrite injected text; `::before` survives that. Scoped to test remarks, so other RichText
 * fields and the Print theme keep their own empty state.
 */
const _TEST_ACCEPTANCE_CSS = /*css*/`
.test-acceptance-result { flex: 0 1 22rem; min-width: 0; }
.test-acceptance-remarks-toggle { white-space: nowrap; }
.test-acceptance-column { padding-bottom: 0; }
.test-acceptance-remarks { padding-top: 0; }
.test-acceptance-remarks .pict-editor { position: relative; padding-top: 0.5rem; }
.test-acceptance-remarks .pict-editor-controls { position: absolute; right: 0; bottom: 100%; margin-bottom: 0.25rem; }
.test-acceptance-remarks .pict-section-form-richtext-view.is-empty { font-size: 0; }
.test-acceptance-remarks .pict-section-form-richtext-view.is-empty::before { content: "No remarks yet — choose Edit to add notes. Markdown formatting such as bold, lists and links is supported."; font-size: 0.95rem; line-height: 1.55; }
`;

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'SampleTestAcceptance',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	// Join the save-data lifecycle so onBeforeSaveDataAsync fires (MarkNewTestsInProgress).
	AutoSaveDataWithApp: true,
};

/**
 * Unified test acceptance input — the Picker-stack port of the legacy SampleTestResult (a Select-family
 * subclass; this class mirrors it 1:1 for diff-review) plus the remarks disclosure.
 *
 * The test result and its remark render as one control at the end of the test's own section instead of
 * an appended "Test Results" section. The remark itself stays a sibling `RichText` descriptor — the
 * markdown editor owns its own descriptor identity — and this provider owns the disclosure that shows
 * and hides it, keyed by `PictForm.RemarksInputHash`.
 *
 * Tracking GUIDs use the SAME `MATERIALS-ENTITY-TRACKING-GUID-TEST-…` element ids as the legacy
 * templates, so a migrated field still binds to a still-legacy sibling Test selector during a phased
 * rollout.
 *
 * Cross-provider contract: SampleStatusPicker / SampleStatusSelector listen for `TestResultChanged` and
 * collect values through `TestStatusAggregate` to derive the sample's status. Both must keep firing in
 * the same shape or sample status auto-derivation silently stops.
 */
class PictProviderInputTypeSampleTestAcceptance extends PickerProvider
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict & { TransactionTracking: any }} */
		this.pict;
		/**
		 * Explicit user choices only, so a render does not collapse an editor the user opened. Absent =
		 * no choice made, and the disclosure follows the stored remark. Never populated from a computed
		 * value: caching the first marshal's answer would freeze a transient read in place.
		 * @type {Record<string, boolean>}
		 */
		this._remarksUserOverride = {};
	}

	/**
	 * @param {string} pInputHTMLID @param {number|string|null} [pRowIndex] @param {string} [pType='TEST']
	 * @return {string}
	 */
	getTrackingGUIDID(pInputHTMLID, pRowIndex, pType = 'TEST')
	{
		const tmpType = pType ? `${pType}-` : '';
		if (pRowIndex != null)
		{
			return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}-${pRowIndex}`;
		}
		return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}`;
	}

	/** Stamp the static result options unless the schema supplied its own. Shared with PickerDisplay,
	 *  which renders this field in Read and Print. */
	_applyTestResultOptions(pInput)
	{
		libTestResultOptions.applyTestResultOptions(pInput, true);
	}

	/** Register the acceptance layout CSS once (idempotent), above the Picker override block. */
	_ensureTestAcceptanceCSS()
	{
		if (this._testAcceptanceCSSInjected)
		{
			return;
		}
		const tmpCSSMap = this.pict && this.pict.CSSMap;
		if (tmpCSSMap && typeof tmpCSSMap.addCSS === 'function')
		{
			tmpCSSMap.addCSS('Headlight-SampleTestAcceptance-CSS', _TEST_ACCEPTANCE_CSS, 610);
			if (typeof tmpCSSMap.injectCSS === 'function')
			{
 tmpCSSMap.injectCSS();
			}
			this._testAcceptanceCSSInjected = true;
		}
	}

	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._ensureTestAcceptanceCSS();
		this._applyTestResultOptions(pInput);
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		// Also applied here, not just on marshal: the column renders visible by default, so waiting for
		// the marshal leaves a collapsed editor briefly on screen.
		this._syncRemarksDisclosure(pView, pInput, null);
		return tmpResult;
	}

	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._ensureTestAcceptanceCSS();
		this._applyTestResultOptions(pInput);
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		this._syncRemarksDisclosure(pView, pInput, pRowIndex);
		return tmpResult;
	}

	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._applyTestResultOptions(pInput);
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput);
		const tmpResult = super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._syncRemarksDisclosure(pView, pInput, null);
		return tmpResult;
	}

	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._applyTestResultOptions(pInput);
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex);
		const tmpResult = super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		this._syncRemarksDisclosure(pView, pInput, pRowIndex);
		return tmpResult;
	}

	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestResultChanged:${this._getTrackingGUID(pInput)}:${pValue}`);
		return tmpResult;
	}

	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		const tmpResult = super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestResultChanged:${this._getTrackingGUID(pInput, pRowIndex)}:${pValue}`);
		return tmpResult;
	}

	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}
		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload, tmpExtraData ] = pEvent.split(':');

		//FIXME: needs revision to work with multi-sample forms (carried over from SampleTestResult).
		if (tmpEventType === 'TestStatusAggregate')
		{
			const tmpDestination = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (Array.isArray(tmpDestination))
			{
				tmpDestination.push(this._getCommittedValue(pView, pInput, pRowIndex));
			}
		}

		if (tmpEventType === 'SetTestResultByHash')
		{
			const tmpChangedTestHash = tmpTrackingGUID;
			const tmpEvent = `GetTestHash:${this._getTrackingGUID(pInput, pRowIndex)}:_GetTestHashResponse.${pInput.Hash}`;
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent);
			const tmpMarshalDestination = pView.getMarshalDestinationObject();
			const tmpTestHash = tmpMarshalDestination._GetTestHashResponse?.[pInput.Hash];
			if (tmpTestHash)
			{
				delete tmpMarshalDestination._GetTestHashResponse[pInput.Hash];
				if (Object.keys(tmpMarshalDestination._GetTestHashResponse).length === 0)
				{
					delete tmpMarshalDestination._GetTestHashResponse;
				}
			}
			if (tmpTestHash !== tmpChangedTestHash)
			{
				return;
			}
			this._setValueAndCommit(pView, pInput, tmpEventPayload, pRowIndex);
			if (tmpExtraData)
			{
				this.pict.manifest.setValueAtAddress(this.pict, tmpExtraData, true);
			}
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestResultChanged:${this._getTrackingGUID(pInput, pRowIndex)}:${tmpEventPayload}`);
		}

		if (tmpEventType === 'MarkNewTestsInProgress' && pInput.PictForm && pInput.PictForm.MarkTestsInProgressOnSave && pInput.PictForm.MarkTestsInProgressOnSave !== 'false')
		{
			const tmpTestResult = this._getCommittedValue(pView, pInput, pRowIndex);
			if (!tmpTestResult || tmpTestResult === 'N/A')
			{
				this._setValueAndCommit(pView, pInput, 'PROG', pRowIndex);
			}
		}
	}

	/**
	 * @param {(pError?: Error) => void} fCallback - The callback to call after the data pre-load.
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		const tmpEvent = 'MarkNewTestsInProgress';
		const tmpTransactionGUID = this.pict.getUUID();
		this.pict.TransactionTracking.registerTransaction(tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(tmpTransactionGUID, () =>
		{
			super.onBeforeSaveDataAsync(fCallback);
		});
		this.pict.views.PictFormMetacontroller.finalizeTransaction(tmpTransactionGUID);
	}

	/** The currently committed result code, read from the marshal destination. */
	_getCommittedValue(pView, pInput, pRowIndex)
	{
		return this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getValueAddress(pInput, pRowIndex));
	}

	/**
	 * The marshal-relative address this field's result code lives at.
	 * @param {Object} pInput @param {number|string|null} [pRowIndex] @return {string}
	 */
	_getValueAddress(pInput, pRowIndex)
	{
		if (pRowIndex != null)
		{
			return this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
		}
		return pInput.DataAddress;
	}

	// --- Remarks disclosure ---------------------------------------------------------------------

	/**
	 * The marshal-relative address the remark lives at. Defaults to the sibling test instance's Notes,
	 * which is where the generated TestInstanceRemarks descriptor wrote it before this input existed.
	 *
	 * @param {Object} pInput @param {number|string|null} [pRowIndex] @return {string}
	 */
	_getRemarksAddress(pInput, pRowIndex)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		if (tmpPictForm.RemarksAddress)
		{
			return tmpPictForm.RemarksAddress;
		}
		const tmpRemarksInput = this._getRemarksInput(pInput);
		if (tmpRemarksInput)
		{
			if (pRowIndex != null)
			{
				return this.pict.providers.Informary.getComposedContainerAddress(tmpRemarksInput.PictForm.InformaryContainerAddress, pRowIndex, tmpRemarksInput.PictForm.InformaryDataAddress);
			}
			return tmpRemarksInput.DataAddress;
		}
		return null;
	}

	/**
	 * The sibling remarks descriptor this input's disclosure controls, named by `PictForm.RemarksInputHash`.
	 * @param {Object} pInput @return {Object|null}
	 */
	_getRemarksInput(pInput)
	{
		const tmpRemarksHash = pInput?.PictForm?.RemarksInputHash;
		if (!tmpRemarksHash)
		{
			return null;
		}
		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors)
		{
			return null;
		}
		const tmpDescriptors = Object.values(tmpGlobalManifest.Descriptors);
		// Standalone form: the configured hash IS the descriptor's hash.
		for (const tmpDescriptor of tmpDescriptors)
		{
			if (tmpDescriptor.Hash === tmpRemarksHash)
			{
				return tmpDescriptor;
			}
		}
		// Container form: each test is injected through createDistinctManifest, which salts every
		// descriptor hash (SampleTestResult -> SampleTestResult_<salt>_<test>) but leaves RemarksInputHash
		// alone, since it is a plain string value and not a hash reference the salter knows about. The
		// pair still shares a group, so resolve there instead of giving up.
		const tmpGroup = pInput?.PictForm?.Group;
		if (!tmpGroup)
		{
			return null;
		}
		const tmpInGroup = tmpDescriptors.filter((pDescriptor) => ((pDescriptor.PictForm || {}).Group === tmpGroup) &&
			((pDescriptor.PictForm || {}).InputType === 'RichText'));
		if (tmpInGroup.length < 1)
		{
			return null;
		}
		const tmpPrefixed = tmpInGroup.filter((pDescriptor) => String(pDescriptor.Hash || '').startsWith(tmpRemarksHash));
		const tmpPool = (tmpPrefixed.length > 0) ? tmpPrefixed : tmpInGroup;
		if (tmpPool.length === 1)
		{
			return tmpPool[0];
		}
		// Several result/remark pairs can share one group. Both halves of a pair carry the same salt and
		// test hash, so the longest shared trailing run picks this input's own remarks field.
		let tmpBest = null;
		let tmpBestLength = -1;
		for (const tmpDescriptor of tmpPool)
		{
			const tmpLength = commonSuffixLength(String(pInput.Hash || ''), String(tmpDescriptor.Hash || ''));
			if (tmpLength > tmpBestLength)
			{
				tmpBestLength = tmpLength;
				tmpBest = tmpDescriptor;
			}
		}
		return tmpBest;
	}

	/** Stable key for one field's disclosure state (tabular rows each get their own). */
	_getRemarksStateKey(pInput, pRowIndex)
	{
		return (pRowIndex != null) ? `${pInput.Hash}-${pRowIndex}` : pInput.Hash;
	}

	/**
	 * The rendered column of the sibling remarks descriptor. Resolved through that descriptor's own
	 * hidden value input, which the editor body always emits under its RawHTMLID.
	 *
	 * @param {Object} pInput @param {number|string|null} pRowIndex @return {HTMLElement|null}
	 */
	_getRemarksColumnElement(pInput, pRowIndex)
	{
		const tmpRemarksInput = this._getRemarksInput(pInput);
		if (!tmpRemarksInput)
		{
			return null;
		}
		let tmpElement = null;
		if (tmpRemarksInput.Macro && tmpRemarksInput.Macro.RawHTMLID)
		{
			let tmpHTMLID = `#${tmpRemarksInput.Macro.RawHTMLID}`;
			if (pRowIndex != null)
			{
				tmpHTMLID = `${tmpHTMLID}-${pRowIndex}`;
			}
			const tmpFound = this.pict.ContentAssignment.getElement(tmpHTMLID);
			tmpElement = Array.isArray(tmpFound) ? tmpFound[0] : tmpFound;
		}
		if (!tmpElement)
		{
			// A container form's injected descriptors carry no Macro — that is added to the view's own
			// copies, not to the manifest the metacontroller holds. Element ids still follow the
			// `<view>-FormInput-<Hash>` convention, so fall back to matching on that.
			const tmpSuffix = (pRowIndex != null) ?
				`FormInput-${tmpRemarksInput.Hash}-${pRowIndex}` :
				`FormInput-${tmpRemarksInput.Hash}`;
			const tmpMatches = this.pict.ContentAssignment.getElement(`[id$="${tmpSuffix}"]`);
			tmpElement = Array.isArray(tmpMatches) ? tmpMatches[0] : tmpMatches;
		}
		if (!tmpElement || typeof tmpElement.closest !== 'function')
		{
			return null;
		}
		return tmpElement.closest('.column');
	}

	/**
	 * Show or hide the remarks editor. Called from the disclosure control in the template.
	 *
	 * @param {string} pInputHash - The acceptance descriptor's Hash.
	 * @param {number|string} [pRowIndex]
	 */
	toggleRemarks(pInputHash, pRowIndex)
	{
		const tmpInput = this._getInputByHash(pInputHash);
		if (!tmpInput)
		{
			this.log.warn('SampleTestAcceptance: no descriptor to toggle remarks for.', { pInputHash });
			return;
		}
		const tmpKey = this._getRemarksStateKey(tmpInput, pRowIndex);
		const tmpOpen = !this._isRemarksOpen(tmpInput, pRowIndex);
		this._remarksUserOverride[tmpKey] = tmpOpen;
		this._applyRemarksOpen(tmpInput, pRowIndex, tmpOpen);
	}

	/**
	 * Re-apply the disclosure after a marshal. A render wipes DOM classes, so the open state lives on the
	 * provider and is re-applied here rather than being read back off the element.
	 *
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 */
	_syncRemarksDisclosure(pView, pInput, pRowIndex)
	{
		// Some older test schemas carry a result with no remarks field at all. A disclosure that opens
		// nothing reads as a broken control, so hide it rather than wire it to empty.
		if (!this._getRemarksInput(pInput))
		{
			this._setRemarksToggleVisible(pInput, pRowIndex, false);
			return;
		}
		const tmpKey = this._getRemarksStateKey(pInput, pRowIndex);
		// Recomputed every marshal: a user choice wins, otherwise follow the stored remark so a remark
		// that arrives later (autofill, a solver, a reloaded document) is never left hidden.
		const tmpOpen = (typeof this._remarksUserOverride[tmpKey] === 'boolean') ?
			this._remarksUserOverride[tmpKey] :
			this._shouldOpenRemarks(pView, pInput, pRowIndex);
		this._applyRemarksOpen(pInput, pRowIndex, tmpOpen);
	}

	/** Current disclosure state: the user's choice when they have made one, else the stored remark. */
	_isRemarksOpen(pInput, pRowIndex)
	{
		const tmpKey = this._getRemarksStateKey(pInput, pRowIndex);
		if (typeof this._remarksUserOverride[tmpKey] === 'boolean')
		{
			return this._remarksUserOverride[tmpKey];
		}
		const tmpColumn = this._getRemarksColumnElement(pInput, pRowIndex);
		return tmpColumn ? !tmpColumn.classList.contains('is-hidden') : false;
	}

	/**
	 * Whether the remarks editor should open on first marshal. Today: only when a remark is already
	 * stored. The "remarks required per result code" flag, when it lands, feeds this one predicate.
	 *
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex @return {boolean}
	 */
	_shouldOpenRemarks(pView, pInput, pRowIndex)
	{
		const tmpRemarksAddress = this._getRemarksAddress(pInput, pRowIndex);
		if (!tmpRemarksAddress)
		{
			return false;
		}
		const tmpRemark = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRemarksAddress);
		return (typeof tmpRemark === 'string') && (tmpRemark.trim() !== '');
	}

	/** The disclosure control's element id for this field. */
	_getRemarksToggleID(pInput, pRowIndex)
	{
		if (pRowIndex != null)
		{
			return `#TEST-ACCEPTANCE-REMARKS-TOGGLE-${pInput.Macro.RawHTMLID}-${pRowIndex}`;
		}
		return `#TEST-ACCEPTANCE-REMARKS-TOGGLE-${pInput.Macro.RawHTMLID}`;
	}

	/**
	 * The disclosure control for this field. The click path reaches the provider through
	 * `_getInputByHash`, which returns the metacontroller's descriptor — and on a container form that
	 * copy carries no Macro. Fall back to the `...-FormInput-<Hash>` id convention, as the remarks
	 * column lookup does, rather than dereferencing Macro.
	 *
	 * @param {Object} pInput @param {number|string|null} pRowIndex @return {HTMLElement|null}
	 */
	_getRemarksToggleElement(pInput, pRowIndex)
	{
		let tmpFound = null;
		if (pInput && pInput.Macro && pInput.Macro.RawHTMLID)
		{
			tmpFound = this.pict.ContentAssignment.getElement(this._getRemarksToggleID(pInput, pRowIndex));
		}
		else if (pInput && pInput.Hash)
		{
			const tmpSuffix = (pRowIndex != null) ? `FormInput-${pInput.Hash}-${pRowIndex}` : `FormInput-${pInput.Hash}`;
			tmpFound = this.pict.ContentAssignment.getElement(`[id^="TEST-ACCEPTANCE-REMARKS-TOGGLE-"][id$="${tmpSuffix}"]`);
		}
		return (Array.isArray(tmpFound) ? tmpFound[0] : tmpFound) || null;
	}

	/** @param {Object} pInput @param {number|string|null} pRowIndex @param {boolean} pVisible */
	_setRemarksToggleVisible(pInput, pRowIndex, pVisible)
	{
		const tmpToggle = this._getRemarksToggleElement(pInput, pRowIndex);
		if (tmpToggle)
		{
			tmpToggle.classList.toggle('is-hidden', !pVisible);
		}
	}

	/** @param {Object} pInput @param {number|string|null} pRowIndex @param {boolean} pOpen */
	_applyRemarksOpen(pInput, pRowIndex, pOpen)
	{
		const tmpColumn = this._getRemarksColumnElement(pInput, pRowIndex);
		const tmpToggle = this._getRemarksToggleElement(pInput, pRowIndex);
		if (tmpColumn)
		{
			tmpColumn.classList.toggle('is-hidden', !pOpen);
			// Pull the editor up under the result row and lift its View/Edit switch into it. Only under
			// the standard layout — the vertical template stacks differently and is left alone.
			const tmpOwnColumn = tmpToggle && (typeof tmpToggle.closest === 'function') ? tmpToggle.closest('.column') : null;
			if (tmpOwnColumn && tmpOwnColumn.classList.contains('test-acceptance-column'))
			{
				tmpColumn.classList.add('test-acceptance-remarks');
			}
		}
		const tmpChevron = tmpToggle && tmpToggle.querySelector('i');
		if (tmpChevron)
		{
			tmpChevron.className = pOpen ? 'fas fa-fw fa-chevron-down' : 'fas fa-fw fa-chevron-right';
		}
	}

	/** @param {string} pInputHash @return {Object|null} */
	_getInputByHash(pInputHash)
	{
		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors)
		{
			return null;
		}
		for (const tmpDescriptor of Object.values(tmpGlobalManifest.Descriptors))
		{
			if (tmpDescriptor.Hash === pInputHash)
			{
				return tmpDescriptor;
			}
		}
		return null;
	}

	// --- Tracking GUID binding (ported from SampleTestResult) ------------------------------------

	/**
	 * @param {Object} pInput @param {number|string|null} [pRowIndex] @param {string} [pType='TEST']
	 * @return {string|null}
	 */
	_getTrackingGUID(pInput, pRowIndex, pType = 'TEST')
	{
		if (pRowIndex == null)
		{
			if (pType === 'TEST' && pInput.PictForm?.TestTrackingGUID && pInput.PictForm.TestTrackingGUID != true)
			{
				return pInput.PictForm.TestTrackingGUID;
			}
			if (pInput.PictForm?.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
			{
				return pInput.PictForm.SampleTrackingGUID;
			}
		}
		const tmpGUID = this.pict.ContentAssignment.readContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType));
		if (typeof tmpGUID === 'string' && tmpGUID.length > 0)
		{
			return tmpGUID;
		}
		return null;
	}

	/**
	 * @param {Object} pInput @param {number|string|null} pRowIndex @param {string} pTrackingGUID
	 * @param {string} [pType='TEST']
	 */
	_setTrackingGUID(pInput, pRowIndex, pTrackingGUID, pType = 'TEST')
	{
		this.pict.ContentAssignment.assignContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType), pTrackingGUID);
	}

	/**
	 * Inherit the tracking GUID of the test descriptor this result belongs to, so the test's events reach
	 * this field. Searches the row's own group first (tabular), then the global manifest.
	 *
	 * @param {Object} pView @param {Object} pGroup @param {Object} pInput @param {number} [pRowIndex]
	 */
	_bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex)
	{
		if (this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}

		if (pRowIndex != null)
		{
			const tmpLocalManifest = pGroup.supportingManifest;
			if (!tmpLocalManifest || typeof tmpLocalManifest !== 'object' || Array.isArray(tmpLocalManifest))
			{
				this.log.error('SampleTestAcceptance: invalid supporting manifest on group.', { pGroup });
			}
			else
			{
				for (const tmpDescriptor of Object.values(tmpLocalManifest.elementDescriptors))
				{
					if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.TestTrackingGUID)
					{
						const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, pRowIndex);
						if (tmpTrackingGUID)
						{
							this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
							return;
						}
					}
				}
			}
		}

		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors || typeof tmpGlobalManifest.Descriptors !== 'object' || Array.isArray(tmpGlobalManifest.Descriptors))
		{
			this.log.error('SampleTestAcceptance: invalid global manifest on metacontroller.', { pGroup });
			return;
		}
		for (const tmpDescriptor of Object.values(tmpGlobalManifest.Descriptors))
		{
			if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.TestTrackingGUID)
			{
				const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor);
				if (tmpTrackingGUID)
				{
					this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
					return;
				}
			}
		}
		if (!this._getTrackingGUID(pInput, pRowIndex))
		{
			this.log.warn('SampleTestAcceptance: could not find sibling test descriptor to bind tracking GUID from.');
		}
	}
}

module.exports = PictProviderInputTypeSampleTestAcceptance;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
module.exports.DEFAULT_TEST_RESULT_OPTIONS = libTestResultOptions.DEFAULT_TEST_RESULT_OPTIONS;
